import { Component, Inject, OnInit } from "@angular/core";
import { LandingpageAffiliateLogoComponent } from "../landingpage-affiliate-logo/landingpage-affiliate-logo.component";
import { Button } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { LoadingCyberaComponent } from "../../shared/components/loading-cybera/loading-cybera.component";
import { NgClass } from "@angular/common";
import { PasswordModule } from "primeng/password";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertService } from "../../b2b/ops/shared/services/alert.service";
import { BackendClientProvider } from "../../services/BackendClientProvider";
import { IBackendClient, ProblemDetails, RegistrationRequest } from "../../services/BackendClient";
import { RoutingNames } from "../../shared/enums/routes-names";
import { MsalService } from "@azure/msal-angular";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { passwordValidator } from "../utils/password-validator";
import { DividerModule } from "primeng/divider";

interface RegisterForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  password: FormControl<string>;
  accept: FormControl<boolean>;
}

/**
 * This component manages the full user registration process: First name, Last name, Email and Password.
 * It is accessed via the route: /registration.
 * Upon successful registration, the user is redirected to the Victim Dashboard.
 */
@Component({
  selector: "app-registration",
  imports: [
    LandingpageAffiliateLogoComponent,
    Button,
    CheckboxModule,
    InputTextModule,
    LoadingCyberaComponent,
    PasswordModule,
    ReactiveFormsModule,
    NgClass,
    IconFieldModule,
    InputIconModule,
    DividerModule,
  ],
  templateUrl: "./registration.component.html",
  styleUrl: "./registration.component.scss",
})
export class RegistrationComponent implements OnInit {
  protected form: FormGroup<RegisterForm>;
  protected readonly minPasswordLength: number = 8;
  protected readonly maxPasswordLength: number = 64;
  protected error: boolean = false;
  protected isLoading: boolean = false;
  protected readonly affiliateSource: string;
  protected hasSourceParam: boolean;
  protected readonly premiumParam: string;

  public constructor(
    protected msal: MsalService,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly route: ActivatedRoute,
    @Inject(BackendClientProvider) private backendClient: IBackendClient,
  ) {
    this.affiliateSource = this.route.snapshot.queryParamMap.get("source");
    this.premiumParam = this.route.snapshot.queryParamMap.get("premium");
    this.hasSourceParam = !!this.affiliateSource;
  }

  public ngOnInit(): void {
    this.form = new FormGroup<RegisterForm>({
      email: new FormControl<string>(null, [Validators.required, Validators.email]),
      firstName: new FormControl<string>(null, [Validators.required]),
      lastName: new FormControl<string>(null, [Validators.required]),
      password: new FormControl<string>(null, [Validators.required, passwordValidator]),
      accept: new FormControl<boolean>(false, [Validators.requiredTrue]),
    });

    this.form.valueChanges.subscribe(() => {
      if (this.form.valid) {
        this.error = false;
      }
    });
  }

  protected register() {
    this.error = false;
    if (this.form.invalid) {
      this.error = true;
      this.form.markAllAsTouched();
      this.form.controls.accept.markAsDirty();
      return;
    }
    this.error = false;
    this.isLoading = true;
    const value = this.form.value;

    const request = new RegistrationRequest({
      email: value.email,
      password: value.password,
      firstName: value.firstName,
      lastName: value.lastName,
      affiliateLink: this.affiliateSource,
    });
    this.backendClient.registerUser(request).subscribe({
      next: () => {
        this.onRegisterSuccess();
        this.cleanForm();
        this.alertService.success("Registration successful! You'll be redirected soon.");
      },
      error: (err: ProblemDetails) => {
        this.isLoading = false;
        if (err.status === 409) {
          this.alertService.error("User is already registered");
        } else {
          this.alertService.error("Something went wrong");
        }
      },
      complete: () => (this.isLoading = false),
    });
  }

  private cleanForm() {
    this.form.controls.email.reset();
    this.form.controls.firstName.reset();
    this.form.controls.lastName.reset();
    this.form.controls.password.reset();
    this.form.controls.accept.reset();
  }

  private onRegisterSuccess(): void {
    this.router.navigate([RoutingNames.B2C_PERSONAL_ROOT]).catch(() => {
      this.alertService.error("Navigation Error");
    });
  }
}
